import { Link } from "react-router-dom";

type Node =
  | { type: "text"; value: string }
  | { type: "bold"; children: Node[] }
  | { type: "link"; anchor: string; href: string };

// Removes markdown emphasis/heading markers from a plain string (for titles, alt text, meta).
export function stripMarkdown(text: string): string {
  if (!text) return "";
  return text
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, "$1");
}

// Parses inline markdown: bold (**text** / __text__) and links [anchor](href).
function parse(text: string): Node[] {
  const nodes: Node[] = [];
  const re = /\*\*(.+?)\*\*|__(.+?)__|\[([^\]]+)\]\(([^)\s]+)\)/gs;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) nodes.push({ type: "text", value: text.slice(lastIndex, m.index) });
    if (m[1] !== undefined) nodes.push({ type: "bold", children: parse(m[1]) });
    else if (m[2] !== undefined) nodes.push({ type: "bold", children: parse(m[2]) });
    else nodes.push({ type: "link", anchor: m[3], href: m[4] });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) nodes.push({ type: "text", value: text.slice(lastIndex) });
  return nodes;
}

function render(nodes: Node[]) {
  return nodes.map((n, i) => {
    if (n.type === "text") return <span key={i}>{n.value}</span>;
    if (n.type === "bold") return <strong key={i} className="font-semibold text-foreground">{render(n.children)}</strong>;
    if (n.href.startsWith("/")) {
      return (
        <Link key={i} to={n.href} className="text-accentInk font-medium hover:underline underline-offset-2">
          {n.anchor}
        </Link>
      );
    }
    return (
      <a
        key={i}
        href={n.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-accentInk font-medium hover:underline underline-offset-2"
      >
        {n.anchor}
      </a>
    );
  });
}

export function RichText({ text, className }: { text: string; className?: string }) {
  if (!text) return null;
  // Strip leading markdown heading markers — headings are styled by the layout.
  const cleaned = text.replace(/^\s{0,3}#{1,6}\s+/gm, "");
  return <span className={className}>{render(parse(cleaned))}</span>;
}
